import { Fragment, useState } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import * as tracker from '../_core/common/eventTracking/tracker';
import { trellisBreakpoint } from '../_core/utils/responsiveBreakpoints';
import FeatureRow from './FeatureRow';
import { foundationsPaddingLeft, foundationsRowHeaderStyles } from './sharedComponents';
import { filterFeaturesHiddenFromLang } from './utils';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ExpandableSectionLabel = styled.tbody.withConfig({
  displayName: "ExpandableSection__ExpandableSectionLabel"
})(({
  theme
}) => ({
  display: 'block',
  '&:first-of-type': {
    marginTop: theme.space['1000']
  },
  '& tr td:nth-child(1) div > div': Object.assign({
    width: '100%',
    textAlign: 'left',
    paddingTop: theme.space['200'],
    paddingBottom: theme.space['200']
  }, foundationsPaddingLeft, {
    display: 'flex',
    alignItems: 'flex-end'
  }),
  tr: {
    minHeight: theme.space['1400'],
    display: 'table',
    width: '100%',
    borderBottom: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`
  }
}));
export const ExpandableSectionContent = styled.tbody.withConfig({
  displayName: "ExpandableSection__ExpandableSectionContent"
})(({
  isFeatureContentExpanded,
  numberOfRowsToShow,
  theme,
  isInbound25PricingPage
}) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'table',
  width: '100%',
  tableLayout: 'fixed',
  background: isInbound25PricingPage ? theme.color['fill-surface-default'] : undefined,
  transition: 'max-height 0.2s linear',
  '&::after': {
    visibility: isFeatureContentExpanded ? 'hidden' : 'collapse',
    position: 'absolute',
    bottom: '0',
    height: '100%',
    width: '100%',
    content: '',
    pointerEvents: 'none'
  },
  '& tr td': {
    textAlign: 'center'
  },
  '& tr th[scope="row"]': Object.assign({}, foundationsRowHeaderStyles),
  tr: {
    minHeight: theme.space['1400']
  },
  'tr td': {
    borderLeft: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`
  },
  [`tr:nth-child(n + ${numberOfRowsToShow + 1})`]: {
    display: isFeatureContentExpanded ? 'visible ' : 'none '
  }
}));
export const ExpandSectionButtonContainer = styled.tbody.withConfig({
  displayName: "ExpandableSection__ExpandSectionButtonContainer"
})(({
  theme,
  hideFeatureContentExpandedButton
}) => ({
  display: 'block',
  minHeight: hideFeatureContentExpandedButton ? theme.space['600'] : theme.space['1400'],
  '& tr td': {
    paddingBottom: theme.space['600'],
    paddingLeft: theme.space['1200'],
    [trellisBreakpoint.tablet]: {
      paddingLeft: theme.space['400']
    }
  }
}));
export const ExpandSectionButtonRow = styled.tr.withConfig({
  displayName: "ExpandableSection__ExpandSectionButtonRow"
})(({
  theme,
  hideFeatureContentExpandedButton
}) => ({
  textAlign: 'left',
  minHeight: hideFeatureContentExpandedButton ? theme.space['600'] : theme.space['1400'],
  display: 'table'
}));
const SectionLabelH3 = styled.h3.withConfig({
  displayName: "ExpandableSection__SectionLabelH3"
})(({
  theme
}) => ({
  marginBottom: theme.space['0'],
  fontSize: theme.typography['heading-300'].fontSize,
  fontWeight: theme.typography['heading-300'].fontWeight
}));
const ExpandableSection = ({
  featureTableCategory,
  tableSharedData,
  id,
  showLabel = true,
  canViewNewSeats = false,
  onExpand,
  onCollapse,
  defaultNumberOfRowsToShow = 5,
  isInbound25PricingPage = false,
  productLevels
}) => {
  var _productLevels$length;
  const [isFeatureContentExpanded, setIsFeatureContentExpanded] = useState(featureTableCategory.alwaysExpanded || false);
  const {
    LabelContent,
    apiNames: apiNamesBySection
  } = featureTableCategory;
  const features = filterFeaturesHiddenFromLang(featureTableCategory.features);
  const {
    subscreen
  } = tableSharedData;
  const apiNamesToUse = apiNamesBySection;
  const numColumns = (_productLevels$length = productLevels === null || productLevels === void 0 ? void 0 : productLevels.length) !== null && _productLevels$length !== void 0 ? _productLevels$length : apiNamesToUse && apiNamesToUse.length;
  const hideFeatureContentExpandedButton = features.length <= defaultNumberOfRowsToShow;
  const hasPinnedRows = tableSharedData.staticDisplayRowCount && tableSharedData.staticDisplayRowCount > 0;
  const pinnedFeatures = hasPinnedRows ? features.slice(0, tableSharedData.staticDisplayRowCount) : [];
  const overflowFeatures = hasPinnedRows ? features.slice(tableSharedData.staticDisplayRowCount) : features;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [showLabel && /*#__PURE__*/_jsx(ExpandableSectionLabel, {
      children: /*#__PURE__*/_jsx("tr", {
        children: /*#__PURE__*/_jsx("td", {
          colSpan: numColumns + 1,
          children: /*#__PURE__*/_jsx(SectionLabelH3, {
            children: LabelContent
          })
        })
      })
    }, `feature-table-${id}-section-label`), hasPinnedRows && /*#__PURE__*/_jsx(ExpandableSectionContent, {
      "data-test-id": `feature-table-${id}-pinned-section-content`,
      isFeatureContentExpanded: true,
      "data-feature-content-expanded": true,
      numberOfRowsToShow: pinnedFeatures.length,
      isInbound25PricingPage: isInbound25PricingPage,
      children: pinnedFeatures.map(feature => {
        return /*#__PURE__*/_jsx(FeatureRow, {
          apiNames: apiNamesToUse,
          feature: feature,
          canViewNewSeats: canViewNewSeats,
          rowIsVisible: true,
          isInbound25PricingPage: isInbound25PricingPage,
          productLevels: productLevels
        }, feature.featureId);
      })
    }, `${id}-pinned-section-content`), /*#__PURE__*/_jsx(ExpandableSectionContent, {
      "data-test-id": `feature-table-${id}-section-content`,
      isFeatureContentExpanded: isFeatureContentExpanded,
      "data-feature-content-expanded": isFeatureContentExpanded,
      numberOfRowsToShow: hasPinnedRows ? 0 : defaultNumberOfRowsToShow,
      isInbound25PricingPage: isInbound25PricingPage,
      children: overflowFeatures.map((feature, index) => {
        return /*#__PURE__*/_jsx(FeatureRow, {
          apiNames: apiNamesToUse,
          feature: feature,
          canViewNewSeats: canViewNewSeats,
          rowIsVisible: isFeatureContentExpanded || index < defaultNumberOfRowsToShow,
          isInbound25PricingPage: isInbound25PricingPage,
          productLevels: productLevels
        }, feature.featureId);
      })
    }, `${id}-section-content`), !featureTableCategory.alwaysExpanded && /*#__PURE__*/_jsx(ExpandSectionButtonContainer, {
      hideFeatureContentExpandedButton: hideFeatureContentExpandedButton,
      children: /*#__PURE__*/_jsx(ExpandSectionButtonRow, {
        hideFeatureContentExpandedButton: hideFeatureContentExpandedButton,
        children: /*#__PURE__*/_jsx("th", {
          id: "expandable-section-th",
          children: !hideFeatureContentExpandedButton && /*#__PURE__*/_jsx(Button, {
            use: "transparent",
            onClick: e => {
              if (isFeatureContentExpanded) {
                onCollapse === null || onCollapse === void 0 || onCollapse(e);
              } else {
                onExpand === null || onExpand === void 0 || onExpand(e);
              }
              tracker.track('featureTableInteraction', {
                action: `clicked-${isFeatureContentExpanded ? 'collapse' : 'expand'}-table`,
                subCategory: id,
                screen: subscreen
              });
              setIsFeatureContentExpanded(!isFeatureContentExpanded);
            },
            paddingInlineStart: "0",
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              gap: "200",
              children: [isFeatureContentExpanded ? /*#__PURE__*/_jsx(UpIcon, {}) : /*#__PURE__*/_jsx(DownIcon, {}), /*#__PURE__*/_jsx(Text, {
                as: "span",
                textDecoration: "underline",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: isFeatureContentExpanded ? `upgrades.featureTable.body.collapse` : `upgrades.featureTable.body.expand`
                })
              })]
            })
          })
        })
      })
    }, `${id}-section-button-container`)]
  }, `${id}-section`);
};
export default ExpandableSection;